import "zod-openapi/extend";

import { z } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { APILimitsResponse } from "../services/tables/types/tables";
import { APIGetTablesResponseLatest } from "../services/tables/types/tables-latest";

extendZodWithOpenApi(z);

export const TablePermissionSchema = z.union([
  z.literal("add:fields"),
  z.literal("get:fields"),
  z.literal("update:fields"),
  z.literal("delete:fields"),
  z.literal("add:records"),
  z.literal("get:records"),
  z.literal("update:records"),
  z.literal("delete:records"),
  z.literal("add:config"),
  z.literal("get:config"),
  z.literal("update:config"),
  z.literal("add:field_configurations"),
  z.literal("update:field_configurations"),
]);

/**
 * Tables backend uses ULIDs for table IDs.
 * https://gitlab.com/zapier/team-fpv/tablestore/-/blob/master/metastore/types.py#L335
 *
 * TODO: https://zapierorg.atlassian.net/browse/INTRFCS-4550
 */
export const TableIdSchema = z
  .string()
  .min(1)
  .describe("The unique identifier (ULID) of the Zapier Table");

const TablePermissionsSchema = TablePermissionSchema.array();
export type TablePermissions = z.infer<typeof TablePermissionsSchema>;

export const listTablesSchema = z.object({
  permissions: TablePermissionsSchema.optional(),
});

export type ListTablesInput = z.infer<typeof listTablesSchema>;

export const ThinTableSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  created_at: z.string(),
  edited_at: z.string(),
  options: z.record(z.string(), z.any()).optional(),
  kind: z.literal("table"),
  owner_account_id: z.number(),
  owner_user_id: z.number().optional(),
  owner_zapier_customuser_id: z.number(),
});

export const ThinVirtualTableSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  created_at: z.string(),
  edited_at: z.string(),
  options: z.record(z.string(), z.any()),
  parent_table_id: z.string(),
  kind: z.literal("virtual_table"),
  owner_account_id: z.number(),
  owner_user_id: z.number().optional(),
});

export const TableSchema = z.union([ThinTableSchema, ThinVirtualTableSchema]);

export const ListTablesResponseSchema: z.ZodType<
  APIGetTablesResponseLatest["data"]
> = z.array(TableSchema).openapi({ ref: "ListTablesResponse" });

export type ListTablesResponse = z.infer<typeof ListTablesResponseSchema>;

export const TablesLimitsSchema: z.ZodType<APILimitsResponse["data"]> =
  z.object({
    max_table_count: z.number(),
    max_field_count: z.number(),
    max_record_count: z.number(),
    max_view_count: z.number(),
    max_public_link_count: z.number().optional(),
    plan_name: z.string().optional(),
    add_ons_name: z.string().optional(),
    restricted_field_types: z
      .array(
        z.union([
          z.literal("string"),
          z.literal("multiple_string"),
          z.literal("labeled_string"),
          z.literal("multiple_labeled_string"),
          z.literal("text"),
          z.literal("multiple_text"),
          z.literal("boolean"),
          z.literal("multiple_boolean"),
          z.literal("number"),
          z.literal("multiple_number"),
          z.literal("decimal"),
          z.literal("multiple_decimal"),
          z.literal("datetime"),
          z.literal("multiple_datetime"),
          z.literal("uuid"),
          z.literal("multiple_uuid"),
          z.literal("json"),
          z.literal("multiple_json"),
          z.literal("formula"),
          z.literal("button_trigger_zap"),
          z.literal("button_continue_zap"),
          z.literal("email"),
          z.literal("multiple_email"),
          z.literal("link"),
          z.literal("multiple_link"),
          z.literal("currency"),
          z.literal("phone_number"),
          z.literal("ai_formula"),
          z.literal("linked_record"),
          z.literal("multiple_linked_record"),
        ])
      )
      .optional(),
    account_limit_type: z
      .union([
        z.literal(""),
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
        z.literal(5),
        z.literal(99999),
      ])
      .optional(),
    beta_features: z
      .array(
        z.union([z.literal("semantic_search"), z.literal("full_text_search")])
      )
      .optional(),
  });

export const getTableSchema = z.object({
  tableId: TableIdSchema,
});

export const getTableFiltersSchema = z.object({
  tableId: TableIdSchema,
});

export const validateTableDeletionSchema = z.object({
  blockId: z.string(),
});

export const tableInterfacesInputSchema = z.object({
  tableId: TableIdSchema,
});

export const tableInterfacesOutputSchema = z
  .object({
    interfacesProjectName: z.string(),
    interfacesProjectId: z.string(),
    interfacesProjectHref: z.string(),
    interfacesProjectOwnerCustomuserId: z.number(),
    interfacesProjectIsShared: z.boolean(),
    interfacesProjectAccountId: z.number(),
  })
  .array();

export const TableWithFieldsSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    fields: z.array(
      z
        .object({
          id: z.number(),
          name: z.string(),
          // TODO: replicate supported types from tables API?
          type: z.string(),
          options: z.record(z.any(), z.any()).optional(),
        })
        .openapi({ ref: "TableField" })
    ),
  })
  .openapi({ ref: "Table" });
